import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { withRouter } from 'react-router-dom';
import AdvancedCell from 'ps-components/AdvancedTable/AdvancedCell';
import AdvancedRow from 'ps-components/AdvancedTable/AdvancedRow';
import CheckboxInput from 'ps-components/Input/CheckboxInput';
import Search from 'ps-components/Search/Search';
import SlideoutFooter from 'ps-components/Slideout/Footer/SlideoutFooter';
import SlideoutFooterStandardButtons from 'ps-components/Slideout/Footer/SlideoutFooterStandardButtons';
import SlideoutHeaderButton from 'ps-components/Slideout/Header/SlideoutHeaderButton';
import SlideoutSubmitSuccess from 'ps-components/Slideout/SubmitSuccess/SlideoutSubmitSuccess';
import Table from 'ps-components/Table/Table/Table';
import TableBody from 'ps-components/Table/TableBody/TableBody';
import TableHeader from 'ps-components/Table/TableHeader/TableHeader';
import {
  deleteNotActiveSurveys,
  getNotActiveSurveys,
  resetDeleteStatus,
  setSelectedSurveys
} from '../SurveyLibraryChangeActions';
import { SLIDEOUTS as MarketPaySlideouts } from '../../Slideout/Slideouts';
import './NotActiveSurveys.scss';

const COLUMNS = [' ', 'Not Yet Active Surveys'];
const HEADER_TEXT = 'Remove Survey(s)';
const SUB_HEADER_TEXT = 'Select the Not Yet Active Surveys you would like to remove from your Survey Library.';

export const NotActiveSurveys = props => {
  const [availableSurveys, setAvailableSurveys] = useState([]);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (props.slideout.open) {
      props.actions.getNotActiveSurveys();
    } else {
      setSearchText('');
      props.actions.resetDeleteStatus();
    }
  }, [props.slideout]);

  useEffect(() => {
    if (props.surveys) {
      setAvailableSurveys(props.surveys);
    }
  }, [props.surveys]);

  function clearSearch() {
    setSearchText('');
    setAvailableSurveys(props.surveys);
  }

  function getSurveyIds(surveys) {
    return surveys.map(survey => survey.id);
  }

  function hideSlideout() {
    props.hideSlideout();
    props.actions.setSelectedSurveys([]);
  }

  function onCheck(id) {
    let newSelectedSurveys;
    const selectedSurveyIds = getSurveyIds(props.selectedSurveys);
    if (selectedSurveyIds.includes(id)) {
      newSelectedSurveys = props.selectedSurveys.filter(cut => cut.id !== id);
    } else {
      const survey = availableSurveys.find(cut => cut.id === id);
      if (survey) {
        newSelectedSurveys = [...props.selectedSurveys, survey];
      }
    }
    props.actions.setSelectedSurveys(newSelectedSurveys);
  }

  function renderRows() {
    return availableSurveys.map(datum => {
      return (
        <AdvancedRow key={datum.id}>
          <AdvancedCell width="5%">
            <CheckboxInput
              checked={getSurveyIds(props.selectedSurveys).includes(datum.id)}
              className="data__cell-checkbox-input"
              id={datum.id}
              onClick={onCheck}
            />
          </AdvancedCell>
          <AdvancedCell width="95%">
            <div className="not-active-survey__row" key={datum.id}>
              <div className="secondary__data">{datum.code}</div>
              <div className="primary__data">{datum.name}</div>
            </div>
          </AdvancedCell>
        </AdvancedRow>
      );
    });
  }

  function searchSurveys(e) {
    const filteredSurveys = props.surveys.filter(survey => {
      const lowercasedValue = e.target.value.toLowerCase();
      return survey.name.toLowerCase().includes(lowercasedValue) || survey.code.toLowerCase().includes(lowercasedValue);
    });

    setSearchText(e.target.value);
    setAvailableSurveys(filteredSurveys);
  }

  function submitChanges() {
    const { actions, selectedSurveys } = props;
    actions.deleteNotActiveSurveys(selectedSurveys);
  }

  function renderSuccessPage() {
    props.refreshSurveyTabData();

    return (
      <SlideoutSubmitSuccess
        className="delete-surveys__success"
        submitSuccessTitle={`You have successfully deleted ${props.selectedSurveys.length} survey(s)!`}
        submitSuccessDetails={''}
        classNameDetails="delete-surveys__success__details"
        actionText="Done"
        classNameAction="delete-surveys__success__action"
        actionFunction={hideSlideout}
      />
    );
  }

  function renderSelectionPage() {
    return (
      <React.Fragment>
        <div className="header">
          <section className="header-panel__left">
            <div className="header__label">{HEADER_TEXT}</div>
            <div className="sub-header__label">{SUB_HEADER_TEXT}</div>
            <Search
              className="not-active-surveys__search"
              id="not-active-surveys__search"
              onChange={searchSurveys}
              onClick={clearSearch}
              testId="not-active-surveys__search"
              value={searchText}
            />
          </section>
          <section className="header-panel__right">
            <SlideoutHeaderButton iconClass="icon-cancel" onClick={hideSlideout} />
          </section>
        </div>
        <Table>
          <TableHeader cellClass="not-active-surveys__column" columns={COLUMNS} />
          <TableBody>{renderRows()}</TableBody>
        </Table>
        <SlideoutFooter>
          <SlideoutFooterStandardButtons
            applyText="Submit Request"
            doApply={submitChanges}
            doCancel={hideSlideout}
            isApplyDisabled={props.selectedSurveys.length === 0}
          />
        </SlideoutFooter>
      </React.Fragment>
    );
  }

  return <div className="not-active-surveys">{props.deleteSuccess ? renderSuccessPage() : renderSelectionPage()}</div>;
};

NotActiveSurveys.propTypes = {
  actions: shape({
    deleteNotActiveSurveys: func,
    getNotActiveSurveys: func,
    setSelectedSurveys: func
  }),
  deleteSuccess: bool,
  selectedSurveys: arrayOf(
    shape({
      code: string,
      id: number,
      name: string
    })
  ),
  surveys: arrayOf(
    shape({
      code: string,
      id: number,
      name: string
    })
  )
};

const mapStateToProps = ({ surveyLibraryChange }) => ({
  selectedSurveys: surveyLibraryChange.notActiveSelectedSurveys,
  surveys: surveyLibraryChange.notActiveSurveys,
  deleteSuccess: surveyLibraryChange.isDeleted
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      deleteNotActiveSurveys,
      getNotActiveSurveys,
      resetDeleteStatus,
      setSelectedSurveys
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(NotActiveSurveys));
